import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';

import { LOCATION_STATUS, type LocationId } from '@brightlocal/shared';

import { locationQuery } from '../locationQuery';

interface RequireNonAdhocLocationOptions {
  /** TanStack Router fallback target the redirect points to. Defaults to the app root. */
  fallbackRoute?: string;
}

/**
 * Route-level guard for a TanStack Router `loader`: redirects ad-hoc locations away from
 * routes they must not reach. An ad-hoc location's report runs are paid with credits, which
 * the New Platform does not handle yet.
 * Loaders run in parallel, so the location resolves through the `$id` loader's in-flight
 * `locationQuery` request and a failed location is requested once. Throws a redirect for an
 * ad-hoc location.
 */
export const requireNonAdhocLocation = async (
  queryClient: QueryClient,
  locationId: LocationId,
  options?: RequireNonAdhocLocationOptions
) => {
  const location = await queryClient.ensureQueryData(locationQuery(locationId));

  // `null` is the soft-fail sentinel other consumers of this cache key may have stored for a
  // recoverable fetch error (e.g. `useReportStatusPolling` polls with `softFail: true`) — the
  // guard fails open on it rather than blocking navigation on a stale error.
  if (location == null) {
    return;
  }

  if (location.status === LOCATION_STATUS.ADHOC) {
    throw redirect({ to: options?.fallbackRoute ?? '/' });
  }
};
